import { LIMITE_PROXY } from "./imagenAvatar";

/**
 * Preparación de una imagen que se va a subir a Storage **conservando su forma**.
 *
 * `prepararAvatar` recorta al cuadrado central, que es justo lo que no se puede
 * hacer aquí: una foto de fachada recortada a cuadrado pierde los lados, y un
 * plano de planta recortado deja de ser el plano. Esto solo reduce el tamaño
 * manteniendo la proporción.
 *
 * El motivo de reducir es el mismo que en avatares: el proxy que está frente a
 * Supabase en Preview corta el cuerpo de la petición en 1 MB, y ese 413 llega
 * sin cabeceras CORS, así que el navegador no lo puede leer y `fetch` falla con
 * un "Failed to fetch" que no dice nada. Ver `preview-limite-subida-1mb` y
 * `Ejecuciones_manuales/20260831_vps_limite_subida_1mb.md`.
 *
 * Con el video no hay equivalente: no se puede recomprimir en el navegador a un
 * costo razonable, y por eso la pestaña de multimedia lo pide por enlace.
 */

/**
 * Tope del archivo de ORIGEN: solo para no reventar la memoria del navegador.
 *
 * Se exporta para que quien ofrezca el selector de archivos use ESTE número y no
 * uno propio: si el formulario deja elegir una imagen más grande, se rechaza aquí
 * abajo cuando el usuario ya esperó por nada.
 */
export const MAX_BYTES_IMAGEN_ORIGEN = 25 * 1024 * 1024;

export interface ImagenListaParaSubir {
  blob: Blob;
  tipo: string;
  extension: "webp" | "jpg";
  /** Para poder decirle al usuario cuánto se redujo, en vez de hacerlo callado. */
  bytesOriginal: number;
}

const soportaWebp = (() => {
  let cache: boolean | null = null;
  return () => {
    if (cache !== null) return cache;
    try {
      const c = document.createElement("canvas");
      c.width = 1;
      c.height = 1;
      cache = c.toDataURL("image/webp").startsWith("data:image/webp");
    } catch {
      cache = false;
    }
    return cache;
  };
})();

/** Decodifica respetando la orientación EXIF, para que las fotos verticales no se acuesten. */
async function decodificar(file: File) {
  if (typeof createImageBitmap === "function") {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
      return { fuente: bitmap as CanvasImageSource, ancho: bitmap.width, alto: bitmap.height };
    } catch {
      // Safari viejo no acepta las opciones: se cae al <img> de abajo.
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error("El archivo no es una imagen que se pueda abrir."));
      el.src = url;
    });
    return { fuente: img as CanvasImageSource, ancho: img.naturalWidth, alto: img.naturalHeight };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function aBlob(lienzo: HTMLCanvasElement, tipo: string, calidad: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    lienzo.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("No se pudo convertir la imagen."))),
      tipo,
      calidad,
    );
  });
}

/**
 * Deja la imagen por debajo del techo del proxy sin deformarla.
 *
 * `ladoMaximo` acota el lado mayor. 1600 px basta para ver una fachada o leer un
 * plano a pantalla completa; subir el original de 4000 px solo gasta ancho de
 * banda y choca con el límite.
 */
export async function prepararImagenParaSubir(
  file: File,
  ladoMaximo = 1600,
): Promise<ImagenListaParaSubir> {
  if (file.size > MAX_BYTES_IMAGEN_ORIGEN) {
    throw new Error(
      `La imagen pesa ${(file.size / 1024 / 1024).toFixed(1)} MB y es demasiado grande para procesarla. Usa una de menos de 25 MB.`,
    );
  }

  const { fuente, ancho, alto } = await decodificar(file);
  if (!ancho || !alto) throw new Error("El archivo no es una imagen válida.");

  // WebP conserva transparencia; JPG no. Un plano suele venir en PNG con fondo
  // transparente, y sin esto acabaría con el fondo en negro.
  const webp = soportaWebp();
  const tipo = webp ? "image/webp" : "image/jpeg";
  const extension: "webp" | "jpg" = webp ? "webp" : "jpg";

  /** Dibuja a `lado` px de lado mayor y devuelve el mejor blob que quepa, o null. */
  const intentar = async (lado: number): Promise<Blob | null> => {
    const escala = Math.min(1, lado / Math.max(ancho, alto));
    const anchoFinal = Math.max(1, Math.round(ancho * escala));
    const altoFinal = Math.max(1, Math.round(alto * escala));

    const lienzo = document.createElement("canvas");
    lienzo.width = anchoFinal;
    lienzo.height = altoFinal;
    const ctx = lienzo.getContext("2d");
    if (!ctx) throw new Error("Tu navegador no permitió procesar la imagen.");

    if (!webp) {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, anchoFinal, altoFinal);
    }
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(fuente, 0, 0, ancho, alto, 0, 0, anchoFinal, altoFinal);

    // Se baja la calidad por pasos solo si hace falta; a 1600 px la primera suele
    // quedar muy por debajo del megabyte.
    for (const calidad of [0.85, 0.72, 0.6, 0.45]) {
      const blob = await aBlob(lienzo, tipo, calidad);
      if (blob.size <= LIMITE_PROXY) return blob;
    }
    return null;
  };

  try {
    // Si ni al 45 % de calidad cabe, se reduce el lado antes de rendirse. Pasa con
    // fotos muy grandes y con mucho detalle (una fachada con vegetación, un render
    // con ruido): a 1600 px no bajan del megabyte por más que se degrade la
    // calidad, y degradarla más se ve peor que verlas algo más chicas.
    //
    // Antes esto era un error y el usuario se quedaba sin subir la imagen. Para una
    // galería que hoy acepta cualquier cosa, fallar es peor que servirla a 1120 px.
    for (const lado of [ladoMaximo, Math.round(ladoMaximo * 0.7), Math.round(ladoMaximo * 0.5)]) {
      const blob = await intentar(lado);
      if (blob) return { blob, tipo, extension, bytesOriginal: file.size };
    }
  } finally {
    if ("close" in fuente && typeof (fuente as ImageBitmap).close === "function") {
      (fuente as ImageBitmap).close();
    }
  }

  throw new Error(
    "No se pudo comprimir la imagen por debajo de 1 MB, que es el techo del proxy de Preview. Prueba con una versión más chica.",
  );
}

/* ------------------------------------------------------------------ */
/* Puerta única para subir cualquier archivo                           */
/* ------------------------------------------------------------------ */

export interface ArchivoListoParaSubir {
  blob: Blob;
  tipo: string;
  /** Sin punto. La de la imagen procesada, o la del archivo original. */
  extension: string;
  bytesOriginal: number;
  /** false = el archivo viaja tal cual. Sirve para no prometer una reducción que no hubo. */
  comprimida: boolean;
}

/**
 * Formatos de imagen que se dejan pasar intactos:
 *
 * - `image/gif`: el canvas se queda con UN fotograma. Comprimir un GIF animado lo
 *   convierte en una foto fija, y eso no es optimizar, es romperlo.
 * - `image/svg+xml`: es texto, no una retícula de píxeles. Pasarlo por el canvas lo
 *   rasteriza y pierde justo lo que lo hace ligero y nítido a cualquier tamaño.
 */
const IMAGENES_QUE_NO_SE_TOCAN = new Set(["image/gif", "image/svg+xml"]);

/**
 * Lo que debe llamar cualquier pantalla antes de subir un archivo a Storage.
 *
 * Las imágenes rasterizadas se reescalan y recomprimen; todo lo demás —PDF, Office,
 * video, GIF, SVG— viaja intacto y lo dice en `comprimida`. No es pereza: en el
 * navegador no hay forma barata de recomprimir un PDF o un video sin degradarlos, y
 * fingir que sí acabaría subiendo archivos peores del mismo tamaño.
 *
 * Nunca falla por culpa de la compresión: si la imagen no se deja procesar (formato
 * raro, canvas bloqueado), se sube el original. Perder la subida es peor que perder
 * los kilobytes.
 */
export async function prepararArchivoParaSubir(
  file: File,
  ladoMaximo = 1600,
): Promise<ArchivoListoParaSubir> {
  const intacto: ArchivoListoParaSubir = {
    blob: file,
    tipo: file.type || "application/octet-stream",
    extension: (file.name.split(".").pop() || "bin").toLowerCase(),
    bytesOriginal: file.size,
    comprimida: false,
  };

  if (!file.type.startsWith("image/") || IMAGENES_QUE_NO_SE_TOCAN.has(file.type)) {
    return intacto;
  }

  try {
    const img = await prepararImagenParaSubir(file, ladoMaximo);
    return { ...img, bytesOriginal: file.size, comprimida: true };
  } catch (e) {
    console.warn("[imagen] no se pudo comprimir, se sube el original:", file.name, e);
    return intacto;
  }
}
